import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import {
  downloadPilotWeeklyDigestCsv,
  getPilotBiClubs,
  getPilotWeeklyDigest,
  postChampionSignal,
} from "../../components/funtionsBD/Mainservice";
import "./BusinessIntelligence.css";

const EMPTY_SIGNAL = {
  time_to_start_min: "",
  biggest_friction: "",
  would_run_next_week: "",
  missing_feature: "",
  quote: "",
};

const TABS = [
  { id: "summary", label: "Summary" },
  { id: "users", label: "By user" },
  { id: "days", label: "By day" },
  { id: "screens", label: "Screens & features" },
  { id: "champion", label: "Champion signal" },
];

/** Human labels for Gemini feature keys in usage meters. */
const GEMINI_FEATURE_LABELS = {
  score_ocr: "Score OCR (umpire board)",
  chat: "Pingflo chat",
  tts: "Tour TTS",
  home_insight: "Home insight",
  caption: "Clip caption",
  eval_video: "Eval video",
};

function featureLabel(key) {
  return GEMINI_FEATURE_LABELS[key] || key;
}

function mondayUtcIso(dateLike) {
  const source = dateLike ? new Date(dateLike) : new Date();
  const utc = new Date(
    Date.UTC(source.getUTCFullYear(), source.getUTCMonth(), source.getUTCDate())
  );
  const day = utc.getUTCDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  utc.setUTCDate(utc.getUTCDate() + mondayOffset);
  return utc.toISOString().slice(0, 10);
}

function shiftWeekIso(weekStart, deltaWeeks) {
  const monday = mondayUtcIso(weekStart);
  const d = new Date(`${monday}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 7 * Number(deltaWeeks || 0));
  return d.toISOString().slice(0, 10);
}

function weekLabel(weekStart) {
  if (!weekStart) return "—";
  const start = new Date(`${weekStart}T00:00:00.000Z`);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const fmt = (d) =>
    d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  return `${fmt(start)} – ${fmt(end)}`;
}

function money(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `$${Number(n).toFixed(4)}`;
}

function moneyShort(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(3)}`;
  return `$${v.toFixed(4)}`;
}

function formatWhen(iso) {
  if (!iso) return "No activity yet";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function pctOf(value, max) {
  const m = Number(max) || 0;
  const v = Number(value) || 0;
  if (m <= 0) return 0;
  return Math.max(2, Math.round((100 * v) / m));
}

function RankBar({ value, max, tone = "signal" }) {
  return (
    <span className="bi-bar" aria-hidden>
      <span
        className={`bi-bar__fill bi-bar__fill--${tone}`}
        style={{ width: `${pctOf(value, max)}%` }}
      />
    </span>
  );
}

function RankRow({
  title,
  meta,
  valueLabel,
  value,
  max,
  tone = "signal",
  rank,
}) {
  return (
    <li className="bi-rank">
      <span className="bi-rank__idx">{String(rank).padStart(2, "0")}</span>
      <div className="bi-rank__body">
        <div className="bi-rank__top">
          <span className="bi-rank__title">{title}</span>
          <span className="bi-rank__val">{valueLabel}</span>
        </div>
        <RankBar value={value} max={max} tone={tone} />
        {meta ? <p className="bi-rank__meta">{meta}</p> : null}
      </div>
    </li>
  );
}

export function BusinessIntelligence() {
  const [clubs, setClubs] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [weekStart, setWeekStart] = useState(null);
  const [digest, setDigest] = useState(null);
  const [tab, setTab] = useState("summary");
  const [listError, setListError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signal, setSignal] = useState(EMPTY_SIGNAL);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    getPilotBiClubs()
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || res.data || {};
        const list = Array.isArray(data.clubs) ? data.clubs : [];
        setClubs(list);
        if (!selectedId && list.length) {
          setSelectedId(list[0].club_id);
          if (list[0].week_start) setWeekStart(list[0].week_start);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setListError(err?.message || "Could not load pilot clubs");
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetailError("");
    getPilotWeeklyDigest(selectedId, weekStart || undefined)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || res.data;
        setDigest(data);
        if (data?.sheet?.week_start && weekStart == null) {
          setWeekStart(data.sheet.week_start);
        }
        if (data?.champion_signal) {
          setSignal({ ...EMPTY_SIGNAL, ...data.champion_signal });
        } else {
          setSignal(EMPTY_SIGNAL);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDetailError(err?.message || "Could not load digest");
          setDigest(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, weekStart]);

  const filteredClubs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clubs;
    return clubs.filter(
      (c) =>
        String(c.club_name || "")
          .toLowerCase()
          .includes(q) ||
        String(c.club_id || "")
          .toLowerCase()
          .includes(q) ||
        String(c.club_country || "")
          .toLowerCase()
          .includes(q)
    );
  }, [clubs, query]);

  const maxClubUsd = useMemo(
    () => Math.max(0, ...clubs.map((c) => Number(c.gemini_usd) || 0)),
    [clubs]
  );

  const sheet = digest?.sheet || null;
  const currentMonday = mondayUtcIso();
  const canGoNext = sheet?.week_start && sheet.week_start < currentMonday;
  const selectedClub =
    clubs.find((c) => c.club_id === selectedId) ||
    (sheet?.club_id
      ? { club_id: sheet.club_id, club_name: sheet.club_name }
      : null);

  const featureRows = useMemo(
    () =>
      Object.entries(digest?.gemini_by_feature || {}).sort(
        (a, b) => (b[1].prompt_tokens || 0) - (a[1].prompt_tokens || 0)
      ),
    [digest]
  );

  const userRows = digest?.gemini_by_user || [];
  const dayRows = digest?.gemini_by_day || [];
  const screenRows = digest?.screens_by_route || [];

  const maxUserUsd = Math.max(0, ...userRows.map((r) => Number(r.usd) || 0));
  const maxDayUsd = Math.max(0, ...dayRows.map((r) => Number(r.usd) || 0));
  const maxFeatureTok = Math.max(
    0,
    ...featureRows.map(([, r]) => Number(r.prompt_tokens) || 0)
  );
  const maxScreenMin = Math.max(
    0,
    ...screenRows.map((r) => Number(r.dwell_min) || 0)
  );

  const goWeek = (delta) => {
    const base = sheet?.week_start || weekStart || currentMonday;
    setWeekStart(shiftWeekIso(base, delta));
    setDigest(null);
  };

  const handleCsv = async () => {
    try {
      await downloadPilotWeeklyDigestCsv(
        selectedId,
        sheet?.week_start || weekStart || undefined
      );
    } catch (err) {
      setDetailError(err?.message || "Could not download CSV");
    }
  };

  const handleSaveSignal = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    setSaving(true);
    setDetailError("");
    try {
      await postChampionSignal(selectedId, signal);
    } catch (err) {
      setDetailError(err?.message || "Could not save champion signal");
    } finally {
      setSaving(false);
    }
  };

  const selectClub = (club) => {
    setSelectedId(club.club_id);
    setWeekStart(null);
    setDigest(null);
    setTab("summary");
  };

  return (
    <AdminLayout>
      <div className="bi-root">
        <header className="bi-hero">
          <div className="bi-hero__copy">
            <p className="bi-hero__eyebrow">Super Admin · Pilot cohort</p>
            <h1 className="bi-hero__title">Business Intelligence</h1>
            <p className="bi-hero__sub">
              Pick a design-partner club, then read the weekly digest — cost,
              capacity, who used which AI features, and where people spent time.
            </p>
          </div>
          <div className="bi-hero__stat" aria-hidden={listLoading}>
            <span className="bi-hero__stat-k">Pilot clubs</span>
            <span className="bi-hero__stat-v">
              {listLoading ? "…" : filteredClubs.length}
            </span>
          </div>
        </header>

        <div className="bi-shell">
          <aside className="bi-rail" aria-label="Pilot clubs">
            <div className="bi-rail__head">
              <p className="bi-rail__label">Pilot clubs</p>
              <input
                className="bi-rail__search"
                type="search"
                placeholder="Search club…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search pilot clubs"
              />
            </div>
            {listError ? (
              <p className="bi-error bi-pad">{listError}</p>
            ) : null}
            <ul className="bi-rail__list">
              {listLoading
                ? [0, 1, 2].map((i) => (
                    <li key={i} className="bi-skel bi-skel--club" />
                  ))
                : null}
              {!listLoading && !filteredClubs.length ? (
                <li className="bi-muted bi-pad">
                  No pilot clubs with meters yet.
                </li>
              ) : null}
              {filteredClubs.map((club) => {
                const active = club.club_id === selectedId;
                return (
                  <li key={club.club_id}>
                    <button
                      type="button"
                      className={`bi-club${active ? " is-active" : ""}`}
                      onClick={() => selectClub(club)}
                    >
                      <div className="bi-club__row">
                        <p className="bi-club__name">
                          <span
                            className={`bi-club__dot${
                              club.has_activity ? " is-live" : ""
                            }`}
                            aria-hidden
                          />
                          {club.club_name || club.club_id}
                        </p>
                        <span className="bi-club__usd">
                          {moneyShort(club.gemini_usd)}
                        </span>
                      </div>
                      <RankBar
                        value={club.gemini_usd}
                        max={maxClubUsd}
                        tone={active ? "ink" : "signal"}
                      />
                      <div className="bi-club__meta">
                        <span>{club.sessions || 0} sessions</span>
                        <span>peak {club.peak_ccu || 0}</span>
                        {club.club_country ? (
                          <span>{club.club_country}</span>
                        ) : null}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <section className="bi-stage" aria-live="polite">
            {!selectedId ? (
              <div className="bi-empty">
                <p className="bi-empty__title">Select a pilot club</p>
                <p className="bi-empty__sub">
                  The weekly data digest opens here — summary meters, then
                  user, day, and screen detail.
                </p>
              </div>
            ) : (
              <>
                <div className="bi-stage__bar">
                  <div>
                    <p className="bi-stage__kicker">Weekly digest</p>
                    <h2 className="bi-stage__club">
                      {selectedClub?.club_name ||
                        sheet?.club_name ||
                        selectedId}
                    </h2>
                  </div>
                  <div className="bi-week">
                    <button
                      type="button"
                      onClick={() => goWeek(-1)}
                      disabled={detailLoading}
                      aria-label="Previous week"
                    >
                      ←
                    </button>
                    <span className="bi-week__label">
                      {weekLabel(sheet?.week_start || weekStart)}
                    </span>
                    <button
                      type="button"
                      onClick={() => goWeek(1)}
                      disabled={detailLoading || !canGoNext}
                      aria-label="Next week"
                    >
                      →
                    </button>
                    <button
                      type="button"
                      className="bi-btn bi-btn--ghost bi-btn--compact"
                      onClick={() => {
                        setWeekStart(null);
                        setDigest(null);
                      }}
                      disabled={detailLoading}
                    >
                      Latest
                    </button>
                  </div>
                </div>

                {digest?.auto_selected ? (
                  <p className="bi-banner">
                    {digest.current_week_missing_meters
                      ? `Current week (${digest.requested_week_start}) has no usage meters — showing the latest week with activity.`
                      : `Current week (${digest.requested_week_start}) is empty — showing the latest week with data.`}
                  </p>
                ) : null}
                {detailError ? (
                  <p className="bi-error bi-pad">{detailError}</p>
                ) : null}

                {detailLoading && !sheet ? (
                  <div className="bi-meters bi-meters--skel">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div key={i} className="bi-skel bi-skel--meter" />
                    ))}
                  </div>
                ) : null}

                {sheet ? (
                  <>
                    <div
                      className={`bi-meters${
                        detailLoading ? " is-refreshing" : ""
                      }`}
                    >
                      <div className="bi-meter">
                        <p className="bi-meter__k">Gemini cost</p>
                        <p className="bi-meter__v is-signal">
                          {money(sheet.gemini_usd)}
                        </p>
                        <p className="bi-meter__h">
                          {(
                            sheet.gemini_prompt_tokens?.toLocaleString?.() ||
                            sheet.gemini_prompt_tokens
                          )}{" "}
                          in · {sheet.gemini_candidate_tokens} out
                        </p>
                      </div>
                      <div className="bi-meter">
                        <p className="bi-meter__k">Score OCR</p>
                        <p className="bi-meter__v is-signal">
                          {money(sheet.score_ocr_usd)}
                        </p>
                        <p className="bi-meter__h">
                          {sheet.score_ocr_calls || 0} calls ·{" "}
                          {(
                            sheet.score_ocr_prompt_tokens?.toLocaleString?.() ||
                            sheet.score_ocr_prompt_tokens ||
                            0
                          )}{" "}
                          in · {sheet.score_ocr_candidate_tokens || 0} out
                        </p>
                      </div>
                      <div className="bi-meter">
                        <p className="bi-meter__k">Sessions</p>
                        <p className="bi-meter__v">{sheet.sessions}</p>
                        <p className="bi-meter__h">
                          {sheet.players_checked_in} checked in · $
                          {sheet.estimated_session_usd}/session
                        </p>
                      </div>
                      <div className="bi-meter">
                        <p className="bi-meter__k">Peak CCU</p>
                        <p className="bi-meter__v">{sheet.peak_ccu}</p>
                        <p className="bi-meter__h">
                          coaches {sheet.peak_coaches} · players{" "}
                          {sheet.peak_players}
                        </p>
                      </div>
                      <div className="bi-meter">
                        <p className="bi-meter__k">Infra</p>
                        <p className="bi-meter__v">
                          {money(
                            Number(sheet.s3_usd || 0) +
                              Number(sheet.pose_usd || 0)
                          )}
                        </p>
                        <p className="bi-meter__h">
                          S3 {sheet.s3_gb} GB · pose {sheet.pose_jobs} jobs
                        </p>
                      </div>
                    </div>

                    <div className="bi-tabs" role="tablist">
                      {TABS.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          role="tab"
                          aria-selected={tab === t.id}
                          className={`bi-tab${
                            tab === t.id ? " is-active" : ""
                          }`}
                          onClick={() => setTab(t.id)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>

                    <div key={tab} className="bi-panel bi-panel--enter">
                      {tab === "summary" ? (
                        <>
                          <div className="bi-facts">
                            <div className="bi-fact">
                              <span className="bi-fact__k">Tour completed</span>
                              <span className="bi-fact__v">
                                {sheet.tour_completed}
                              </span>
                            </div>
                            <div className="bi-fact">
                              <span className="bi-fact__k">HR samples</span>
                              <span className="bi-fact__v">
                                {sheet.hr_samples}
                              </span>
                            </div>
                            <div className="bi-fact">
                              <span className="bi-fact__k">Eval sessions</span>
                              <span className="bi-fact__v">
                                {sheet.eval_uploads}/
                                {sheet.eval_quota === -1
                                  ? "∞"
                                  : sheet.eval_quota}
                              </span>
                            </div>
                            <div className="bi-fact">
                              <span className="bi-fact__k">
                                $ / player-hour
                              </span>
                              <span className="bi-fact__v">
                                {sheet.estimated_player_hour_usd ?? "—"}
                              </span>
                            </div>
                            <div className="bi-fact">
                              <span className="bi-fact__k">
                                Score OCR share
                              </span>
                              <span className="bi-fact__v">
                                {sheet.gemini_usd > 0
                                  ? `${Math.round(
                                      (100 * (Number(sheet.score_ocr_usd) || 0)) /
                                        Number(sheet.gemini_usd)
                                    )}%`
                                  : "—"}
                              </span>
                            </div>
                            <div className="bi-fact bi-fact--wide">
                              <span className="bi-fact__k">
                                Last club activity
                              </span>
                              <span className="bi-fact__v bi-fact__v--text">
                                {formatWhen(selectedClub?.last_activity_at)}
                              </span>
                            </div>
                          </div>
                          <div className="bi-actions">
                            <button
                              type="button"
                              className="bi-btn"
                              onClick={handleCsv}
                              disabled={!selectedId}
                            >
                              Download weekly CSV
                            </button>
                          </div>
                        </>
                      ) : null}

                      {tab === "users" ? (
                        userRows.length ? (
                          <ul className="bi-ranks">
                            {userRows.map((row, i) => (
                              <RankRow
                                key={row.user_id || row.display_name}
                                rank={i + 1}
                                title={row.display_name || row.user_id}
                                value={row.usd}
                                max={maxUserUsd}
                                valueLabel={money(row.usd)}
                                meta={`${row.events} calls · ${(
                                  row.prompt_tokens?.toLocaleString?.() ||
                                  row.prompt_tokens
                                )} in · ${row.candidate_tokens} out${
                                  row.features
                                    ? ` · ${Object.keys(row.features).join(
                                        ", "
                                      )}`
                                    : ""
                                }`}
                              />
                            ))}
                          </ul>
                        ) : (
                          <p className="bi-muted">
                            No Gemini usage for this week.
                          </p>
                        )
                      ) : null}

                      {tab === "days" ? (
                        dayRows.length ? (
                          <ul className="bi-ranks">
                            {dayRows.map((row, i) => (
                              <RankRow
                                key={row.day}
                                rank={i + 1}
                                title={row.day}
                                value={row.usd}
                                max={maxDayUsd}
                                tone="ink"
                                valueLabel={money(row.usd)}
                                meta={`${row.events} calls · ${(
                                  row.prompt_tokens?.toLocaleString?.() ||
                                  row.prompt_tokens
                                )} in · ${row.candidate_tokens} out`}
                              />
                            ))}
                          </ul>
                        ) : (
                          <p className="bi-muted">
                            No daily Gemini rows this week.
                          </p>
                        )
                      ) : null}

                      {tab === "screens" ? (
                        <div className="bi-split">
                          <section>
                            <p className="bi-rail__label">Gemini features</p>
                            {featureRows.length ? (
                              <ul className="bi-ranks">
                                {featureRows.map(([feat, row], i) => (
                                  <RankRow
                                    key={feat}
                                    rank={i + 1}
                                    title={featureLabel(feat)}
                                    value={row.prompt_tokens}
                                    max={maxFeatureTok}
                                    valueLabel={money(row.usd)}
                                    meta={`${row.events} calls · ${(
                                      row.prompt_tokens?.toLocaleString?.() ||
                                      row.prompt_tokens
                                    )} prompt tokens${
                                      feat === "score_ocr"
                                        ? " · live + post-upload board reads"
                                        : ""
                                    }`}
                                  />
                                ))}
                              </ul>
                            ) : (
                              <p className="bi-muted">
                                No feature breakdown yet.
                              </p>
                            )}
                          </section>
                          <section>
                            <p className="bi-rail__label">Time on screens</p>
                            {screenRows.length ? (
                              <ul className="bi-ranks">
                                {screenRows.map((row, i) => (
                                  <RankRow
                                    key={row.screen}
                                    rank={i + 1}
                                    title={row.screen}
                                    value={row.dwell_min}
                                    max={maxScreenMin}
                                    tone="ink"
                                    valueLabel={`${row.dwell_min} min`}
                                    meta={`${row.views} views · ${row.unique_users} users`}
                                  />
                                ))}
                              </ul>
                            ) : (
                              <p className="bi-muted">
                                Screen dwell appears after users browse with
                                the metering build (routes ≥ 1.5s).
                              </p>
                            )}
                          </section>
                        </div>
                      ) : null}

                      {tab === "champion" ? (
                        <form className="bi-champ" onSubmit={handleSaveSignal}>
                          <h3 className="bi-champ__title">
                            Champion 5-minute signal
                          </h3>
                          <p className="bi-champ__desc">
                            Capture hall-night friction after each session for
                            the Monday review.
                          </p>
                          {[
                            ["time_to_start_min", "Minutes to first table"],
                            ["biggest_friction", "Biggest friction"],
                            [
                              "would_run_next_week",
                              "Run next week without us? (yes/no)",
                            ],
                            ["missing_feature", "Missing feature"],
                            ["quote", "Quote"],
                          ].map(([id, label]) => (
                            <label key={id} htmlFor={`bi-${id}`}>
                              {label}
                              <input
                                id={`bi-${id}`}
                                value={signal[id]}
                                onChange={(e) =>
                                  setSignal((s) => ({
                                    ...s,
                                    [id]: e.target.value,
                                  }))
                                }
                              />
                            </label>
                          ))}
                          <div className="bi-actions">
                            <button
                              type="submit"
                              className="bi-btn"
                              disabled={saving || !selectedId}
                            >
                              {saving ? "Saving…" : "Save champion signal"}
                            </button>
                          </div>
                        </form>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </>
            )}
          </section>
        </div>
      </div>
    </AdminLayout>
  );
}

export default BusinessIntelligence;
